import { useState, useCallback } from 'react'
import { isEmpty } from 'lodash'
import { useTranslations } from 'next-intl'
import { WorkflowType, workflowColumns } from '../models/importModels'
import { getUploadUrlsService, createImportJobService } from '../services/importServices'
import { parseCsvContent, validateCsvHeaders } from '../utils/csvValidation'
import { useInvalidateImportJobs } from './useImports'

export type ImportUploadStep = 'idle' | 'validating' | 'uploading' | 'processing' | 'completed' | 'error'

const ACTIVE_UPLOAD_STEPS: ImportUploadStep[] = ['validating', 'uploading', 'processing']

type UseImportUploadOptions = {
    companyId: string
    onSuccess?: (jobId: string) => void | Promise<void>
    onError?: (error: Error) => void
}

export const useImportUpload = ({ companyId, onSuccess, onError }: UseImportUploadOptions) => {
    const t = useTranslations('imports')
    const invalidateJobs = useInvalidateImportJobs({ companyId })
    const [step, setStep] = useState<ImportUploadStep>('idle')
    const [createdJobId, setCreatedJobId] = useState<string | null>(null)
    const [error, setError] = useState<Error | null>(null)

    const reset = useCallback(() => {
        setStep('idle')
        setError(null)
        setCreatedJobId(null)
    }, [])

    const upload = useCallback(
        async (workflowConfigId: WorkflowType, csvFile: File, assetFiles?: File[]) => {
            let jobId = ''
            try {
                setError(null)
                setStep('validating')

                if (!csvFile.size) {
                    throw new Error(t('errors.csvFileEmpty'))
                }

                const { headers, hasDataRows } = await parseCsvContent(csvFile)
                if (!hasDataRows) {
                    throw new Error(t('errors.csvNoDataRows'))
                }

                const columns = workflowColumns[workflowConfigId]
                if (columns) {
                    const missingRequired = validateCsvHeaders(headers, columns)
                    if (!isEmpty(missingRequired)) {
                        throw new Error(t('errors.missingColumns', { columns: missingRequired.join(', ') }))
                    }
                }

                const allFiles = [csvFile, ...(assetFiles || [])]
                const fileNames = allFiles.map((f) => f.name)

                const urlsResponse = await getUploadUrlsService({
                    companyId,
                    workflow: workflowConfigId,
                    fileNames
                })
                const uploadUrls = urlsResponse?.body?.uploadUrls

                if (!uploadUrls || uploadUrls.length !== allFiles.length) {
                    throw new Error(t('errors.uploadUrlsFailed'))
                }

                setStep('uploading')

                const responses = await Promise.all(
                    allFiles.map((file, i) =>
                        fetch(uploadUrls[i].uploadUrl, {
                            method: 'PUT',
                            body: file,
                            headers: { 'Content-Type': uploadUrls[i].contentType }
                        })
                    )
                )

                const failedUpload = responses.find((r) => !r.ok)
                if (failedUpload) {
                    throw new Error(t('errors.uploadFailed'))
                }

                setStep('processing')

                const jobResponse = await createImportJobService({
                    companyId,
                    workflow: workflowConfigId,
                    filePath: uploadUrls[0].filePath
                })

                if (!jobResponse?.body) {
                    throw new Error(t('errors.createJobFailed'))
                }

                jobId = jobResponse.body.id
                setCreatedJobId(jobId)
                setStep('completed')
            } catch (err) {
                setStep('error')
                const error = err instanceof Error ? err : new Error(t('errors.uploadFailed'))
                setError(error)
                onError?.(error)
                return
            }

            // The new job joins the list before the caller hears of it.
            await invalidateJobs()
            await onSuccess?.(jobId)
        },
        [companyId, invalidateJobs, onSuccess, onError, t]
    )

    return {
        step,
        upload,
        error,
        reset,
        createdJobId,
        isUploading: ACTIVE_UPLOAD_STEPS.includes(step)
    }
}
